import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { keys } from '../api/queries';

/**
 * Server-side pagination, backed by the query cache.
 *
 * Same return shape as before, so list pages need only add `queryKey`. Pages
 * already seen render instantly from cache; moving to another page keeps the
 * current rows on screen until the next ones arrive (no skeleton flash); and
 * `refetch` invalidates every cached page of the entity, which is also what
 * the entity's mutations do, so a write anywhere refreshes the list here.
 *
 * @param {Function} fetchFn - API function that accepts a params object, e.g. getAccounts(params).
 *   With page/limit params the API returns { data: { data: [...], pagination: { total, totalPages } } };
 *   without them (legacy) it returns { data: [...] }.
 * @param {Object} options
 * @param {string}  options.queryKey                 - The entity name ('accounts', 'tasks', …). Required.
 * @param {number}  [options.initialLimit=25]
 * @param {string}  [options.initialSortBy='name']
 * @param {string}  [options.initialSortOrder='asc']
 * @param {number}  [options.debounceMs=300]
 * @param {Object}  [options.extraParams={}]         - Filters sent with every request.
 */
export default function usePagination(fetchFn, options = {}) {
  const {
    queryKey,
    initialLimit = 25,
    initialSortBy = 'name',
    initialSortOrder = 'asc',
    debounceMs = 300,
    extraParams = {},
  } = options;

  if (!queryKey) {
    throw new Error('usePagination needs options.queryKey (the entity name) so its pages can be cached and invalidated.');
  }

  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  // Compare filters by value, not identity: callers rebuild the object each render.
  const extraParamsKey = JSON.stringify(extraParams);

  // Debounce the search box; a new search starts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [search, debounceMs]);

  // New filters start from page 1 too.
  useEffect(() => {
    setPage(1);
  }, [extraParamsKey]);

  const params = useMemo(() => {
    const p = { page, limit, sortBy, sortOrder, ...JSON.parse(extraParamsKey) };
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [page, limit, sortBy, sortOrder, debouncedSearch, extraParamsKey]);

  const query = useQuery({
    queryKey: keys.list(queryKey, params),
    queryFn: async () => {
      const response = await fetchFn(params);
      if (response.data && response.data.pagination) {
        return {
          data: response.data.data,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages,
        };
      }
      // Legacy flat array — one page.
      const arr = Array.isArray(response.data) ? response.data : [];
      return { data: arr, total: arr.length, totalPages: 1 };
    },
    placeholderData: keepPreviousData,
  });

  const result = query.data || { data: [], total: 0, totalPages: 0 };
  const error = query.error
    ? (query.error.response?.data?.error || query.error.message || 'Failed to load data')
    : '';

  const handleSort = useCallback((field, direction) => {
    setSortBy(field);
    setSortOrder(direction);
    setPage(1);
  }, []);

  const changeLimit = useCallback((newLimit) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  // Every cached page of this entity, not just the one on screen.
  const refetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey: keys.entity(queryKey) }),
    [queryClient, queryKey]
  );

  return {
    // Data
    data: result.data,
    loading: query.isPending,
    fetching: query.isFetching,
    error,
    // Pagination
    page,
    setPage,
    limit,
    setLimit: changeLimit,
    total: result.total,
    totalPages: result.totalPages,
    // Search
    search,
    setSearch,
    // Sort
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    handleSort,
    // Actions
    refetch,
  };
}
